import { Link, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { LogOut, Upload as UploadIcon } from 'lucide-react';
import { useUser } from './auth/useUser';
import { Login } from './pages/Login';
import { Home } from './pages/Home';
import { Movies } from './pages/Movies';
import { MovieDetail } from './pages/MovieDetail';
import { Upload } from './pages/Upload';

export default function App() {
  const { user, signOut } = useUser();

  if (!user) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  return (
    <div className="min-h-full bg-base-100">
      <Header onSignOut={signOut} displayName={user.displayName || user.username || 'You'} />
      <main className="mx-auto max-w-7xl px-4 pb-16 pt-6">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/movies" element={<Movies />} />
          <Route path="/movies/:id" element={<MovieDetail />} />
          <Route path="/upload" element={<Upload />} />
          <Route path="/login" element={<Navigate to="/" replace />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
    </div>
  );
}

function Header({ onSignOut, displayName }: { onSignOut: () => void; displayName: string }) {
  const loc = useLocation();
  const tab = (path: string, label: string) => {
    const active = loc.pathname === path || (path === '/movies' && loc.pathname.startsWith('/movies'));
    return (
      <Link
        to={path}
        className={[
          'rounded-full px-4 py-1.5 text-sm font-medium transition-colors',
          active ? 'bg-primary text-white' : 'text-text-primary hover:bg-surface-2',
        ].join(' ')}
      >
        {label}
      </Link>
    );
  };
  return (
    <header className="sticky top-0 z-20 border-b border-surface-divider bg-base-100/85 backdrop-blur">
      <div className="mx-auto flex max-w-7xl items-center gap-4 px-4 py-3">
        <Link to="/" className="text-lg font-semibold tracking-tight">
          NNi <span className="text-primary">Stream</span>
        </Link>
        <nav className="ml-2 flex items-center gap-1">
          {tab('/', 'Home')}
          {tab('/movies', 'Movies')}
        </nav>
        <div className="ml-auto flex items-center gap-2">
          <Link
            to="/upload"
            className="inline-flex items-center gap-1.5 rounded-full bg-primary px-3 py-1.5 text-sm font-medium text-white hover:bg-primary-hover"
          >
            <UploadIcon size={14} /> Upload
          </Link>
          <span className="hidden text-sm text-text-secondary md:inline">{displayName}</span>
          <button
            type="button"
            onClick={onSignOut}
            className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm text-text-secondary hover:bg-surface-2"
            aria-label="Sign out"
          >
            <LogOut size={14} />
          </button>
        </div>
      </div>
    </header>
  );
}
